/**
 * Parser for Word OOXML that converts track changes to HTML for display
 * Uses DOM-based parsing (like ParagraphXMLDisplay) for reliable text extraction
 */

import { isOurSideTrackChangesAuthor as isOurSideSuggestionAuthor } from '../../utils/quickResponses';

const REF_NOISE_CORE = [
  /\s*REF_Ref\d+\s*/g,
  /\s*REF\s+_Ref\d+\s*\\r\s*\\h\s*[\s\u200E\u200F]*/g,
  /\bREF\s+[\s\S]*?MERGEFORMAT/gi,
  /\s*REF\s*\S+\s*(?:\\[^\s]*[\s\u200E\u200F]*)*MERGEFORMAT?[\s\u200E\u200F]*/g,
  /\s*\|\s*\\?\*?\s*h\s*\|/gi,
  // Field debris only — do not strip "]" in normal text like "[ארכימדס]"
  /MERGEFORMAT[\s\u200E\u200F]*\]/gi,
  /\?]\s*/g,
] as const;

function applyRefNoisePatterns(s: string): string {
  let out = s;
  for (const re of REF_NOISE_CORE) {
    out = out.replace(re, ' ');
  }
  return out.replace(/\s{2,}/g, ' ');
}

/** Strip REF field code patterns from full plain-text strings (LLM context, etc.). */
function stripRefPatterns(s: string): string {
  if (!s || !s.trim()) return s;
  return applyRefNoisePatterns(s).trim();
}

/**
 * Strip REF from a single w:t / w:delText node.
 * Preserves leading/trailing spaces — Word often stores inter-word spaces in their own runs.
 */
function stripRefPatternsInTextNode(s: string): string {
  if (!s) return s;
  if (!/REF|MERGEFORMAT/i.test(s)) return s;

  const leading = s.match(/^\s*/)?.[0] ?? '';
  const trailing = s.match(/\s*$/)?.[0] ?? '';
  let core = s.slice(leading.length, s.length - trailing.length);
  core = applyRefNoisePatterns(core).trim();
  if (!core) {
    // REF-only run: keep a single space if the run was spacing between words
    return leading || trailing ? leading + trailing : '';
  }
  return leading + core + trailing;
}

/** True when w:t is only a Hebrew appendix letter (א׳ / א') left from a REF field. */
function isAppendixLetterOnly(text: string): boolean {
  const t = text.replace(/[\s\u200E\u200F]/g, '').trim();
  return /^א['׳\u05f3]?$/.test(t);
}

/** Collapse duplicate appendix markers and extra spaces after REF strip (e.g. "נספח  א' א'"). */
function normalizeAppendixFieldText(text: string): string {
  return text
    .replace(/(נספח)\s*(א['׳\u05f3])\s*\1/gi, '$1 $2')
    .replace(/\s{2,}/g, ' ');
}

export interface TrackChangeSegment {
  type: 'insert' | 'delete' | 'normal';
  text: string;
  author?: string;
  date?: string;
}

export class TrackChangesParser {
  /**
   * Strip REF / MERGEFORMAT field noise from plain text (Ask full-doc context, legacy paragraph.text).
   * Aligns with ParagraphXMLDisplay / stripRefPatterns.
   */
  static stripRefFieldNoise(text: string): string {
    return stripRefPatterns(text);
  }

  /**
   * Strip duplicate xmlns / xmlns:prefix attributes on the FIRST element tag.
   *
   * Windows Word occasionally hands back a paragraph like:
   *   <ns1:p xmlns:ns1="…" xmlns:ns1="…" xmlns:w="…" …>
   * which is invalid XML (an attribute cannot be repeated). This corrupts every
   * downstream consumer: DOMParser fails, we fall back to plain-text, and the
   * track-changes generator can't find the paragraph. We defensively de-dupe
   * xmlns declarations on the outer tag before parsing so we can recover from
   * whatever accumulated in stored documents.
   */
  static deduplicateXmlnsAttributes(ooxml: string): string {
    if (!ooxml) return ooxml;
    const firstTagEnd = ooxml.indexOf('>');
    if (firstTagEnd === -1) return ooxml;
    const openTag = ooxml.slice(0, firstTagEnd);
    if (!openTag.includes('xmlns')) return ooxml;

    const seen = new Set<string>();
    const deduped = openTag.replace(/\s(xmlns(?::[A-Za-z0-9_.-]+)?)=("([^"]*)"|'([^']*)')/g, (match, name: string) => {
      if (seen.has(name)) {
        console.warn('[TrackChangesParser] deduplicateXmlnsAttributes: dropped duplicate', name);
        return '';
      }
      seen.add(name);
      return match;
    });
    if (deduped === openTag) return ooxml;
    return deduped + ooxml.slice(firstTagEnd);
  }

  /**
   * Well-known Word / Office namespace URIs for prefixes that may appear in OOXML
   * paragraph fragments without an explicit xmlns: declaration (because Word only
   * declares them on the document root, not on every extracted fragment).
   */
  private static readonly KNOWN_WORD_NAMESPACES: Record<string, string> = {
    w: 'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
    w14: 'http://schemas.microsoft.com/office/word/2010/wordml',
    w15: 'http://schemas.microsoft.com/office/word/2012/wordml',
    w16: 'http://schemas.microsoft.com/office/word/2018/wordml',
    w16cex: 'http://schemas.microsoft.com/office/word/2018/wordml/cex',
    w16cid: 'http://schemas.microsoft.com/office/word/2016/wordml/cid',
    w16sdtdh: 'http://schemas.microsoft.com/office/word/2020/wordml/sdtdatahash',
    w16se: 'http://schemas.microsoft.com/office/word/2015/wordml/symex',
    wpc: 'http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas',
    wpg: 'http://schemas.microsoft.com/office/word/2010/wordprocessingGroup',
    wpi: 'http://schemas.microsoft.com/office/word/2010/wordprocessingInk',
    wps: 'http://schemas.microsoft.com/office/word/2010/wordprocessingShape',
    wp14: 'http://schemas.microsoft.com/office/word/2010/wordprocessingDrawing',
    mc: 'http://schemas.openxmlformats.org/markup-compatibility/2006',
    r: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
    m: 'http://schemas.openxmlformats.org/officeDocument/2006/math',
    v: 'urn:schemas-microsoft-com:vml',
    o: 'urn:schemas-microsoft-com:office:office',
    w10: 'urn:schemas-microsoft-com:office:word',
    wne: 'http://schemas.microsoft.com/office/word/2006/wordml',
    wp: 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing',
    aink: 'http://schemas.microsoft.com/office/drawing/2016/ink',
    am3d: 'http://schemas.microsoft.com/office/drawing/2017/model3d',
    cx: 'http://schemas.microsoft.com/office/drawing/2014/chartex',
    cx1: 'http://schemas.microsoft.com/office/drawing/2015/9/8/chartex',
    cx2: 'http://schemas.microsoft.com/office/drawing/2015/10/21/chartex',
    cx3: 'http://schemas.microsoft.com/office/drawing/2016/5/9/chartex',
    cx4: 'http://schemas.microsoft.com/office/drawing/2016/5/10/chartex',
    cx5: 'http://schemas.microsoft.com/office/drawing/2016/5/11/chartex',
    cx6: 'http://schemas.microsoft.com/office/drawing/2016/5/12/chartex',
    cx7: 'http://schemas.microsoft.com/office/drawing/2016/10/13/chartex',
    cx8: 'http://schemas.microsoft.com/office/drawing/2016/10/14/chartex',
  };

  /**
   * Fix namespace-prefix issues in the opening tag of a standalone OOXML fragment.
   *
   * Word returns paragraph fragments where the `xmlns:w14`, `xmlns:wpc`, etc.
   * declarations are only on the full document root — not repeated on the
   * extracted `<w:p>` element. After our add-in serializes and re-inserts the
   * paragraph, Word may strip some of those declarations so subsequent reads
   * return fragments like:
   *
   *   <w:p xmlns:w="…" w14:paraId="5E75F5DE" w14:textId="2DE9874D" …>
   *
   * This is invalid standalone XML ("Namespace prefix w14 for paraId on p is
   * not defined"). We fix it in two steps:
   *
   * 1. Inject `xmlns:prefix="uri"` for every undeclared prefix whose URI we
   *    know (w14, wpc, mc, r, …).
   * 2. Strip attributes whose prefix is still undeclared after step 1 — they
   *    are metadata we don't need (e.g. w16sdtdh:val on exotic future prefixes).
   *
   * Only the FIRST tag is patched because we only parse paragraph fragments.
   */
  static sanitizeUndeclaredNamespacePrefixes(ooxml: string): string {
    if (!ooxml) return ooxml;

    // Find the opening tag (ends at first '>').
    const tagEnd = ooxml.indexOf('>');
    if (tagEnd === -1) return ooxml;
    const openTag = ooxml.slice(0, tagEnd);

    // Collect already-declared prefixes (xmlns:prefix="…" or xmlns="…").
    const declaredPrefixes = new Set<string>();
    const xmlnsDeclRe = /xmlns(?::([A-Za-z0-9_.-]+))?=/g;
    let m: RegExpExecArray | null;
    while ((m = xmlnsDeclRe.exec(openTag)) !== null) {
      declaredPrefixes.add(m[1] ?? ''); // '' for default namespace
    }

    // Find all prefix:localName attribute usages (skip xmlns:xxx itself).
    const usedPrefixes = new Set<string>();
    const prefixUsageRe = /\s([A-Za-z0-9_.-]+):[A-Za-z0-9_.-]+=["']/g;
    while ((m = prefixUsageRe.exec(openTag)) !== null) {
      const prefix = m[1];
      if (prefix !== 'xmlns') usedPrefixes.add(prefix);
    }

    // Also collect prefixes used in element name itself (e.g. ns1:p).
    const elemNameMatch = openTag.match(/^<([A-Za-z0-9_.-]+):/);
    if (elemNameMatch) usedPrefixes.add(elemNameMatch[1]);

    // Determine which prefixes are missing declarations.
    const undeclared = [...usedPrefixes].filter((p) => !declaredPrefixes.has(p));
    if (undeclared.length === 0) return ooxml;

    console.warn('[TrackChangesParser] sanitizeUndeclaredNamespacePrefixes: found undeclared prefixes', undeclared);

    const known = TrackChangesParser.KNOWN_WORD_NAMESPACES;
    const toInject: string[] = [];
    const toStrip: string[] = [];

    for (const prefix of undeclared) {
      if (known[prefix]) {
        toInject.push(`xmlns:${prefix}="${known[prefix]}"`);
        console.log('[TrackChangesParser] sanitizeUndeclaredNamespacePrefixes: injecting', `xmlns:${prefix}`);
      } else {
        toStrip.push(prefix);
        console.warn('[TrackChangesParser] sanitizeUndeclaredNamespacePrefixes: stripping attrs with unknown prefix', prefix);
      }
    }

    // Build new open tag: inject declarations right after tag name, then strip unknown-prefix attrs.
    let fixed = openTag;

    // Inject xmlns declarations after the element name (first non-space after '<tagName').
    if (toInject.length > 0) {
      fixed = fixed.replace(/^(<[^\s/>]+)/, `$1 ${toInject.join(' ')}`);
    }

    // Strip attributes whose prefix is still undeclared.
    for (const prefix of toStrip) {
      // Removes: prefix:localName="value" or prefix:localName='value'
      const stripRe = new RegExp(`\\s${prefix}:[A-Za-z0-9_.-]+=(\"[^\"]*\"|'[^']*')`, 'g');
      fixed = fixed.replace(stripRe, '');
    }

    if (fixed === openTag) return ooxml;
    return fixed + ooxml.slice(tagEnd);
  }

  /**
   * Sanitize OOXML before accepted-text extraction / track-change generation.
   * Matches dashboard ParagraphXMLDisplay: remove w:instrText and strip REF/MERGEFORMAT
   * from w:t / w:delText so position maps align with extractAcceptedText().
   */
  static sanitizeFieldReferencesInOoxml(ooxml: string): string {
    if (!ooxml?.trim()) return ooxml;
    // Order matters: dedup first (removes duplicate xmlns:ns1), then inject/strip
    // missing prefix declarations (fixes w14:paraId without xmlns:w14), then check
    // for field refs and do the heavier DOM walk only if needed.
    const dedupedOoxml = TrackChangesParser.deduplicateXmlnsAttributes(ooxml);
    const nsSafeOoxml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(dedupedOoxml);
    if (!/REF|MERGEFORMAT|instrText/i.test(nsSafeOoxml)) return nsSafeOoxml;
    ooxml = nsSafeOoxml;

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ooxml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        console.warn('[TrackChangesParser] sanitizeFieldReferencesInOoxml: parse error, using raw OOXML');
        return ooxml;
      }

      const toRemove: Element[] = [];
      const walk = (el: Element) => {
        const localName = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
        const tagName = el.tagName || '';

        if (localName === 'instrtext' || tagName.includes('instrText')) {
          toRemove.push(el);
          return;
        }

        if (localName === 't' || localName === 'deltext' || tagName.endsWith(':t')) {
          let tc = el.textContent || '';
          if (/REF|MERGEFORMAT/i.test(tc)) {
            const stripped = stripRefPatternsInTextNode(tc);
            if (stripped !== tc) {
              console.log('[TrackChangesParser] Stripped REF noise from', localName, {
                beforeLen: tc.length,
                afterLen: stripped.length,
                preview: tc.slice(0, 80),
              });
            }
            tc = stripped;
          }
          const normalized = normalizeAppendixFieldText(tc);
          if (normalized !== tc) {
            el.textContent = normalized;
          } else if (tc !== el.textContent) {
            el.textContent = tc;
          }
        }

        Array.from(el.children).forEach((child) => walk(child as Element));
      };

      walk(xmlDoc.documentElement);
      toRemove.forEach((el) => el.parentNode?.removeChild(el));

      const para =
        xmlDoc.documentElement.localName === 'p'
          ? xmlDoc.documentElement
          : (xmlDoc.querySelector('p') as Element | null);
      if (para) {
        TrackChangesParser.cleanupFieldArtifactsInParagraph(para);
      }

      const out = new XMLSerializer().serializeToString(xmlDoc.documentElement);
      console.log('[TrackChangesParser] sanitizeFieldReferencesInOoxml complete', {
        inLen: ooxml.length,
        outLen: out.length,
        removedInstrText: toRemove.length,
      });
      return out;
    } catch (e) {
      console.warn('[TrackChangesParser] sanitizeFieldReferencesInOoxml failed', e);
      return ooxml;
    }
  }

  /**
   * Inspect w:ins / w:del authors in OOXML — for diagnosing red vs blue preview styling.
   * Blue (track-change-new) requires our-side author (AI Suggestion / User Edit) on the HTML element.
   */
  static auditOoxmlTrackChangeAuthors(ooxml: string): {
    insCount: number;
    delCount: number;
    nestedInsInsideIns: number;
    aiSuggestionIns: number;
    otherIns: number;
    entries: Array<{
      kind: 'ins' | 'del';
      author: string;
      depth: number;
      nestedInsideIns: boolean;
      textLen: number;
      textSnippet: string;
    }>;
  } {
    const snippet = (s: string, max = 60) => {
      const c = (s || '').replace(/\s+/g, ' ').trim();
      return c.length > max ? `${c.slice(0, max)}...` : c;
    };
    const empty = {
      insCount: 0,
      delCount: 0,
      nestedInsInsideIns: 0,
      aiSuggestionIns: 0,
      otherIns: 0,
      entries: [] as Array<{
        kind: 'ins' | 'del';
        author: string;
        depth: number;
        nestedInsideIns: boolean;
        textLen: number;
        textSnippet: string;
      }>,
    };
    if (!ooxml?.trim()) return empty;
    try {
      const xmlDoc = TrackChangesParser.parseOoxmlFragment(ooxml);
      if (!xmlDoc) return empty;
      const walk = (el: Element, depth: number, insideIns: boolean) => {
        const local = el.localName || el.tagName.split(':').pop() || '';
        if (local === 'ins' || local === 'del') {
          const author =
            el.getAttribute('w:author') ||
            el.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
            el.getAttribute('author') ||
            '';
          const text = el.textContent || '';
          const kind = local as 'ins' | 'del';
          if (kind === 'ins') {
            empty.insCount++;
            if (insideIns) empty.nestedInsInsideIns++;
            if (author === 'AI Suggestion') empty.aiSuggestionIns++;
            else empty.otherIns++;
          } else {
            empty.delCount++;
          }
          empty.entries.push({
            kind,
            author,
            depth,
            nestedInsideIns: insideIns,
            textLen: text.length,
            textSnippet: snippet(text),
          });
        }
        const childInsideIns = insideIns || local === 'ins';
        for (const child of Array.from(el.childNodes)) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            walk(child as Element, depth + 1, childInsideIns);
          }
        }
      };
      const root = xmlDoc.documentElement;
      const para =
        root.localName === 'p' ? root : (root.querySelector('p') as Element | null);
      if (para) walk(para, 0, false);
      else walk(root, 0, false);
      return empty;
    } catch {
      return empty;
    }
  }

  /** Count track-change-new (blue) vs legacy (red) markers in preview HTML. */
  static auditHtmlTrackChangeStyling(html: string): {
    insTotal: number;
    insNewBlue: number;
    insLegacyRed: number;
    delTotal: number;
    delNewBlue: number;
    delLegacyRed: number;
    hasAiSuggestionTitle: boolean;
  } {
    if (!html?.trim()) {
      return {
        insTotal: 0,
        insNewBlue: 0,
        insLegacyRed: 0,
        delTotal: 0,
        delNewBlue: 0,
        delLegacyRed: 0,
        hasAiSuggestionTitle: false,
      };
    }
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    const allIns = body.querySelectorAll('ins.track-change-insertion, ins');
    const allDel = body.querySelectorAll('del.track-change-deletion, del');
    let insNewBlue = 0;
    let insLegacyRed = 0;
    allIns.forEach((el) => {
      if (el.classList.contains('track-change-new')) insNewBlue++;
      else insLegacyRed++;
    });
    let delNewBlue = 0;
    let delLegacyRed = 0;
    allDel.forEach((el) => {
      if (el.classList.contains('track-change-new')) delNewBlue++;
      else delLegacyRed++;
    });
    return {
      insTotal: allIns.length,
      insNewBlue,
      insLegacyRed,
      delTotal: allDel.length,
      delNewBlue,
      delLegacyRed,
      hasAiSuggestionTitle: html.includes('Author: AI Suggestion'),
    };
  }

  /**
   * Parse an OOXML fragment. Generated paragraphs often mix a default xmlns on the
   * new <w:p> with cloned children that still use a `w:` prefix (WebKit/Word Mac
   * XMLSerializer). That is invalid standalone XML and used to force the plain-text
   * fallback — mashed "weeks weeks months" with no our-side/their-side redlines.
   * If the first parse fails, wrap in a root that declares the Word namespaces.
   */
  static parseOoxml(ooxml: string): Document | null {
    return TrackChangesParser.parseOoxmlFragment(ooxml);
  }

  private static parseOoxmlFragment(ooxml: string): Document | null {
    const parser = new DOMParser();
    const tryParse = (xml: string): Document | null => {
      const doc = parser.parseFromString(xml, 'text/xml');
      return doc.querySelector('parsererror') ? null : doc;
    };

    const direct = tryParse(ooxml);
    if (direct) return direct;

    const stripped = ooxml.replace(/^<\?xml[^?]*\?>\s*/i, '');
    const known = TrackChangesParser.KNOWN_WORD_NAMESPACES;
    const wrapped =
      `<root xmlns:w="${known.w}" xmlns:w14="${known.w14}" xmlns:r="${known.r}">${stripped}</root>`;
    const retry = tryParse(wrapped);
    if (retry) {
      console.warn('[TrackChangesParser] Parsed OOXML via namespace wrapper (missing xmlns:w on generated fragment)');
    }
    return retry;
  }

  /**
   * Remove OOXML that Word's Range.insertOoxml rejects in a hand-crafted / swapped
   * paragraph package: comment range markers (and the \u0005 comment-ref char),
   * bookmarks, and permission ranges. These are cloned from the live paragraph but
   * the comments.xml / bookmarks part is not in wrapParagraphInPackage → GeneralException.
   */
  static stripMarkupUnsupportedByInsertOoxml(ooxml: string): string {
    if (!ooxml?.trim()) return ooxml;
    const doc = TrackChangesParser.parseOoxmlFragment(ooxml);
    if (!doc) return ooxml;

    const drop = new Set([
      'commentrangestart',
      'commentrangeend',
      'commentreference',
      'bookmarkstart',
      'bookmarkend',
      'permstart',
      'permend',
    ]);
    const toRemove: Element[] = [];
    const walk = (el: Element) => {
      const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
      if (drop.has(local)) {
        toRemove.push(el);
        return;
      }
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child as Element);
        } else if (child.nodeType === Node.TEXT_NODE && child.textContent?.includes('\u0005')) {
          child.textContent = child.textContent.replace(/\u0005/g, '');
        }
      }
    };
    walk(doc.documentElement);
    for (const el of toRemove) {
      el.parentNode?.removeChild(el);
    }

    const W_NS = TrackChangesParser.KNOWN_WORD_NAMESPACES.w;
    const root = doc.documentElement;
    const para =
      root.localName === 'p'
        ? root
        : doc.getElementsByTagNameNS(W_NS, 'p').item(0) ||
          (root.querySelector('p') as Element | null);
    if (!para) return new XMLSerializer().serializeToString(root);
    return TrackChangesParser.ensureXmlnsWOnFragment(
      new XMLSerializer().serializeToString(para),
    );
  }

  private static ensureXmlnsWOnFragment(xml: string): string {
    if (!xml || /xmlns:w=/i.test(xml)) return xml;
    const ns = TrackChangesParser.KNOWN_WORD_NAMESPACES.w;
    const stripped = xml.replace(/^<\?xml[^?]*\?>\s*/i, '');
    const decl = xml.slice(0, xml.length - stripped.length);
    return decl + stripped.replace(/^(<[^\s/>]+)/, `$1 xmlns:w="${ns}"`);
  }

  /**
   * Converts OOXML with track changes to HTML for display
   * Uses DOM-based parsing (like ParagraphXMLDisplay) for better reliability
   */
  static convertOoxmlToHtml(ooxml: string): string {
    if (!ooxml) {
      console.warn('[TrackChangesParser] Empty OOXML provided');
      return '';
    }

    // Apply full namespace sanitization pipeline before any parsing.
    // sanitizeFieldReferencesInOoxml already calls deduplicateXmlnsAttributes and
    // sanitizeUndeclaredNamespacePrefixes internally, but we also dedup+fix the
    // raw ooxml so convertOoxmlToHtml works even when called independently.
    const step1 = TrackChangesParser.deduplicateXmlnsAttributes(ooxml);
    const step2 = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(step1);
    const workingOoxml = TrackChangesParser.sanitizeFieldReferencesInOoxml(step2);
    const ooxmlAuthorAudit = TrackChangesParser.auditOoxmlTrackChangeAuthors(workingOoxml);
    console.log('[TrackChangeAuthor] OOXML audit before HTML conversion', ooxmlAuthorAudit);

    console.log('[TrackChangesParser] Starting conversion, OOXML length:', workingOoxml.length);
    console.log('[TrackChangesParser] OOXML preview (first 500 chars):', workingOoxml.substring(0, 500));
    try {
      const xmlDoc = TrackChangesParser.parseOoxmlFragment(workingOoxml);
      if (!xmlDoc) {
        console.error('[TrackChangesParser] XML parsing error after namespace wrapper retry');
        // Fallback: try to extract text directly
        const fallbackText = this.extractPlainTextFromXml(ooxml);
        console.log('[TrackChangesParser] Using fallback plain text extraction, length:', fallbackText.length);
        return fallbackText;
      }
      
      console.log('[TrackChangesParser] XML parsed successfully');
      console.log('[TrackChangesParser] Document element:', xmlDoc.documentElement.tagName);
      
      // Find the paragraph element (w:p) - it might be the root or nested
      let paragraphElement: Element | null = null;
      
      // Check if documentElement is w:p
      const docLocalName = xmlDoc.documentElement.localName || 
                          xmlDoc.documentElement.tagName.split(':').pop();
      console.log('[TrackChangesParser] Document element local name:', docLocalName);
      
      if (docLocalName === 'p') {
        paragraphElement = xmlDoc.documentElement;
        console.log('[TrackChangesParser] Found paragraph as document element');
      } else {
        // Search for paragraph in the document using TreeWalker
        console.log('[TrackChangesParser] Searching for paragraph element using TreeWalker');
        const walker = xmlDoc.createTreeWalker(
          xmlDoc.documentElement,
          NodeFilter.SHOW_ELEMENT,
          null
        );
        
        let node: Node | null;
        let searchCount = 0;
        while (node = walker.nextNode()) {
          searchCount++;
          const element = node as Element;
          const localName = element.localName || element.tagName.split(':').pop();
          console.log(`[TrackChangesParser] Checking element ${searchCount}: ${element.tagName} (localName: ${localName})`);
          if (localName === 'p') {
            paragraphElement = element;
            console.log('[TrackChangesParser] Found paragraph element at depth:', searchCount);
            break;
          }
        }
        console.log('[TrackChangesParser] Searched', searchCount, 'elements');
      }
      
      if (!paragraphElement) {
        console.warn('[TrackChangesParser] No paragraph element found, extracting plain text');
        const fallbackText = this.extractPlainTextFromXml(ooxml);
        console.log('[TrackChangesParser] Fallback text length:', fallbackText.length);
        return fallbackText;
      }

      // Remove REF field elements (w:instrText) and strip REF from text in w:delText/w:t (like courtyard ParagraphXMLDisplay)
      this.removeRefFromTree(paragraphElement);

      console.log('[TrackChangesParser] Processing paragraph element:', paragraphElement.tagName);
      console.log('[TrackChangesParser] Paragraph element children count:', paragraphElement.childNodes.length);
      
      // Check for track changes in the paragraph
      const hasIns = paragraphElement.querySelector('ins') || 
                    Array.from(paragraphElement.children).some(el => {
                      const localName = el.localName || el.tagName.split(':').pop();
                      return localName === 'ins';
                    });
      const hasDel = paragraphElement.querySelector('del') ||
                    Array.from(paragraphElement.children).some(el => {
                      const localName = el.localName || el.tagName.split(':').pop();
                      return localName === 'del';
                    });
      
      console.log('[TrackChangesParser] Has insertions (w:ins):', !!hasIns);
      console.log('[TrackChangesParser] Has deletions (w:del):', !!hasDel);
      
      // Process the paragraph element using DOM-based approach
      const html = this.processElement(paragraphElement);
      const htmlStylingAudit = TrackChangesParser.auditHtmlTrackChangeStyling(html);
      console.log('[TrackChangeAuthor] HTML styling audit after conversion', {
        ...htmlStylingAudit,
        likelyMisstyledAiInsert:
          ooxmlAuthorAudit.aiSuggestionIns > 0 && htmlStylingAudit.insNewBlue === 0,
        hint:
          ooxmlAuthorAudit.aiSuggestionIns > 0 && htmlStylingAudit.insNewBlue === 0
            ? 'AI w:ins present in OOXML but no track-change-new in HTML — check nested w:ins handling'
            : undefined,
      });
      console.log('[TrackChangesParser] Generated HTML length:', html.length);
      console.log('[TrackChangesParser] Generated HTML preview (first 500 chars):', html.substring(0, 500));
      console.log('[TrackChangesParser] HTML contains track-change classes:', 
        html.includes('track-change-insertion') || html.includes('track-change-deletion'));
      
      return html;
    } catch (error) {
      console.error('[TrackChangesParser] Error parsing OOXML:', error);
      console.error('[TrackChangesParser] Error stack:', error instanceof Error ? error.stack : 'No stack');
      console.error('[TrackChangesParser] OOXML preview (first 500 chars):', ooxml.substring(0, 500));
      // Fallback: extract plain text
      const fallbackText = this.extractPlainTextFromXml(ooxml);
      console.log('[TrackChangesParser] Using fallback, text length:', fallbackText.length);
      return fallbackText;
    }
  }
  
  /**
   * Extract plain text from XML string (removes all tags)
   * Public method for extracting text from OOXML
   */
  static extractText(xml: string): string {
    if (!xml) {
      return '';
    }
    
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(safeXml, 'text/xml');
      
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return this.extractPlainTextFromXml(xml);
      }
      
      const root = xmlDoc.documentElement;
      const rootLocal = root.localName || root.tagName.split(':').pop();
      if (rootLocal === 'p') {
        this.removeRefFromTree(root);
      }
      let text = root.textContent || (root as any).innerText || '';
      if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
        text = stripRefPatterns(text);
      }
      return text;
    } catch (error) {
      return this.extractPlainTextFromXml(xml);
    }
  }
  
  /**
   * Fallback: Extract plain text from XML string (removes all tags)
   */
  private static extractPlainTextFromXml(xml: string): string {
    // Remove all XML tags and decode entities
    const text = xml.replace(/<[^>]+>/g, '');
    const div = document.createElement('div');
    div.innerHTML = text;
    return div.textContent || div.innerText || '';
  }
  
  /**
   * Process an XML element using DOM-based approach (similar to ParagraphXMLDisplay)
   */
  private static processElement(element: Element, depth: number = 0): string {
    const indent = '  '.repeat(depth);
    const localName = element.localName || element.tagName.split(':').pop() || element.tagName;
    const fullTagName = element.tagName;
    
    console.log(`${indent}[TrackChangesParser.processElement] Processing: ${fullTagName} (localName: ${localName})`);
    
    let html = '';
    
    // Handle track changes - these are top-level elements we want to style
    if (localName === 'ins') {
      const insAuthor =
        element.getAttribute('w:author') ||
        element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
        element.getAttribute('author') ||
        '';
      // Use per-child HTML when nested track changes exist. Nested <w:ins> (e.g. AI append
      // inside counterparty w:ins) must not use simple_flatten — that collapses child authors to red.
      const hasNestedDel = this.hasNestedElement(element, 'del');
      const hasNestedIns = this.hasNestedElement(element, 'ins');
      const useNestedChildrenPath = hasNestedDel || hasNestedIns;
      const htmlPath = useNestedChildrenPath ? 'nested_children' : 'simple_flatten';
      console.log(`${indent}[TrackChangeAuthor] w:ins HTML path`, {
        depth,
        author: insAuthor,
        isAiSuggestion: insAuthor === 'AI Suggestion',
        hasNestedDel,
        hasNestedIns,
        htmlPath,
        willGetTrackChangeNewClass:
          insAuthor === 'AI Suggestion' || (hasNestedIns && htmlPath === 'nested_children'),
      });
      console.log(`${indent}[TrackChangesParser] Found insertion element (w:ins)`);
      
      if (useNestedChildrenPath) {
        // Process children individually so each w:ins / w:del keeps its own author styling.
        // Plain <w:r> runs are still part of the parent insertion (counterparty redline).
        console.log(`${indent}[TrackChangesParser] w:ins has nested track changes — processing children individually`);
        const parentAuthor = element.getAttribute('w:author') ||
          element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
          element.getAttribute('author') || '';
        const parentIsNew = isOurSideSuggestionAuthor(parentAuthor);
        const parentClass = parentIsNew ? ' track-change-new' : '';
        const parentChangeId = element.getAttribute('w:id') ||
          element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'id') ||
          element.getAttribute('id') || '';
        const parentDataAttrs = parentIsNew ? ` data-change-id="${parentChangeId}" data-change-type="insertion"` : '';

        for (const child of Array.from(element.childNodes)) {
          if (child.nodeType !== Node.ELEMENT_NODE) continue;
          const childElem = child as Element;
          const childLocal = childElem.localName || childElem.tagName.split(':').pop() || childElem.tagName;
          
          if (childLocal === 'del') {
            let delText = this.extractTextFromElement(childElem, true);
            if (delText && (delText.includes('REF') || delText.includes('MERGEFORMAT'))) {
              delText = stripRefPatternsInTextNode(delText);
            }
            if (delText) {
              const delAuthor = childElem.getAttribute('w:author') || 
                               childElem.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
                               childElem.getAttribute('author') || '';
              const delIsNew = isOurSideSuggestionAuthor(delAuthor);
              const delClass = delIsNew ? ' track-change-new' : '';
              const delChangeId = childElem.getAttribute('w:id') || 
                                 childElem.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'id') ||
                                 childElem.getAttribute('id') || '';
              const delDataAttrs = delIsNew ? ` data-change-id="${delChangeId}" data-change-type="deletion"` : '';
              const delTitleAttr = delAuthor ? ` title="Author: ${this.escapeHtmlAttribute(delAuthor)}"` : '';
              html += `<del class="track-change-deletion${delClass}"${delDataAttrs}${delTitleAttr}>${this.escapeHtml(delText)}</del>`;
            }
          } else if (childLocal === 'ins') {
            const insText = this.extractTextFromElement(childElem, false);
            if (insText) {
              const nestedInsAuthor = childElem.getAttribute('w:author') || 
                                     childElem.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
                                     childElem.getAttribute('author') || '';
              const nestedInsIsNew = isOurSideSuggestionAuthor(nestedInsAuthor);
              const nestedInsClass = nestedInsIsNew ? ' track-change-new' : '';
              const nestedInsChangeId = childElem.getAttribute('w:id') || 
                                       childElem.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'id') ||
                                       childElem.getAttribute('id') || '';
              const nestedInsDataAttrs = nestedInsIsNew ? ` data-change-id="${nestedInsChangeId}" data-change-type="insertion"` : '';
              const nestedInsTitleAttr = nestedInsAuthor ? ` title="Author: ${this.escapeHtmlAttribute(nestedInsAuthor)}"` : '';
              html += `<ins class="track-change-insertion${nestedInsClass}"${nestedInsDataAttrs}${nestedInsTitleAttr}>${this.escapeHtml(insText)}</ins>`;
            }
          } else {
            // Plain run (w:r) — unchanged content but still part of the original insertion (previous redline)
            const childText = this.extractTextFromElement(childElem, false);
            if (childText) {
              const parentTitleAttr = parentAuthor ? ` title="Author: ${this.escapeHtmlAttribute(parentAuthor)}"` : '';
              html += `<ins class="track-change-insertion${parentClass}"${parentDataAttrs}${parentTitleAttr}>${this.escapeHtml(childText)}</ins>`;
            }
          }
        }
      } else {
        // Simple case: no nested deletions — extract all text at once
        let text = this.extractTextFromElement(element, false);
        if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
          text = stripRefPatternsInTextNode(text);
        }
        console.log(`${indent}[TrackChangesParser] Extracted insertion text: "${text}" (length: ${text.length})`);
        if (text) {
          const author = element.getAttribute('w:author') || 
                         element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
                         element.getAttribute('author') || '';
          const isNewlyGenerated = isOurSideSuggestionAuthor(author);
          const additionalClass = isNewlyGenerated ? ' track-change-new' : '';
          const changeId = element.getAttribute('w:id') || 
                          element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'id') ||
                          element.getAttribute('id') || '';
          const dataAttrs = isNewlyGenerated ? ` data-change-id="${changeId}" data-change-type="insertion"` : '';
          const titleAttr = author ? ` title="Author: ${this.escapeHtmlAttribute(author)}"` : '';
          html += `<ins class="track-change-insertion${additionalClass}"${dataAttrs}${titleAttr}>${this.escapeHtml(text)}</ins>`;
          console.log(`${indent}[TrackChangesParser] Added insertion HTML${isNewlyGenerated ? ' (newly generated)' : ''}, author: "${author}", changeId: "${changeId}"`);
        } else {
          console.warn(`${indent}[TrackChangesParser] No text found in insertion element`);
        }
      }
      return html; // Don't process children further
    }
    
    if (localName === 'del') {
      console.log(`${indent}[TrackChangesParser] Found deletion element (w:del)`);
      let text = this.extractTextFromElement(element, true);
      if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
        text = stripRefPatternsInTextNode(text);
      }
      console.log(`${indent}[TrackChangesParser] Extracted deletion text: "${text}" (length: ${text.length})`);
      if (text) {
        // Our-side suggestion edits (AI Suggestion / User Edit) use blue styling.
        const author = element.getAttribute('w:author') || 
                       element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
                       element.getAttribute('author') || '';
        const isNewlyGenerated = isOurSideSuggestionAuthor(author);
        const additionalClass = isNewlyGenerated ? ' track-change-new' : '';
        // Get change ID for identification
        const changeId = element.getAttribute('w:id') || 
                        element.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'id') ||
                        element.getAttribute('id') || '';
        const dataAttrs = isNewlyGenerated ? ` data-change-id="${changeId}" data-change-type="deletion"` : '';
        const titleAttr = author ? ` title="Author: ${this.escapeHtmlAttribute(author)}"` : '';
        html += `<del class="track-change-deletion${additionalClass}"${dataAttrs}${titleAttr}>${this.escapeHtml(text)}</del>`;
        console.log(`${indent}[TrackChangesParser] Added deletion HTML${isNewlyGenerated ? ' (newly generated)' : ''}, author: "${author}", changeId: "${changeId}"`);
      } else {
        console.warn(`${indent}[TrackChangesParser] No text found in deletion element`);
      }
      return html; // Don't process children, already extracted text
    }
    
    // Word field instruction (e.g. REF ... \h \* MERGEFORMAT) — do not render
    if (localName === 'instrText') {
      return html;
    }

    // For text elements (w:t), extract and return the text (REF already stripped by removeRefFromTree; strip again for safety)
    if (localName === 't') {
      let text = TrackChangesParser.getTextNodeContent(element);
      if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
        text = stripRefPatternsInTextNode(text);
      }
      console.log(`${indent}[TrackChangesParser] Found text element (w:t): "${text}"`);
      if (text) {
        html += this.escapeHtml(text);
      }
      return html;
    }

    // For deleted text elements (w:delText), extract and mark as deletion (REF already stripped by removeRefFromTree)
    if (localName === 'delText') {
      let text = element.textContent || '';
      if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
        text = stripRefPatternsInTextNode(text);
      }
      console.log(`${indent}[TrackChangesParser] Found delText element: "${text}"`);
      if (text) {
        html += `<del class="track-change-deletion">${this.escapeHtml(text)}</del>`;
      }
      return html;
    }
    
    // For runs (w:r), check for formatting that indicates track changes
    if (localName === 'r') {
      console.log(`${indent}[TrackChangesParser] Processing run (w:r) with ${element.childNodes.length} children`);
      
      // Check for formatting properties that indicate track changes
      // Find rPr (run properties) element
      const rPr = Array.from(element.children).find(el => {
        const localName = el.localName || el.tagName.split(':').pop();
        return localName === 'rPr';
      }) as Element | undefined;
      
      let isDeletion = false;
      let isInsertion = false;
      
      if (rPr) {
        console.log(`${indent}[TrackChangesParser] Found rPr element with ${rPr.children.length} children`);
        
        // Find strike, underline, and color elements
        const strike = Array.from(rPr.children).find(el => {
          const localName = el.localName || el.tagName.split(':').pop();
          return localName === 'strike';
        }) as Element | undefined;
        
        const underline = Array.from(rPr.children).find(el => {
          const localName = el.localName || el.tagName.split(':').pop();
          return localName === 'u';
        }) as Element | undefined;
        
        const color = Array.from(rPr.children).find(el => {
          const localName = el.localName || el.tagName.split(':').pop();
          return localName === 'color';
        }) as Element | undefined;
        
        // Check if color is red (common for track changes)
        let isRedColor = false;
        let isTrackChangeColor = false; // Any color that indicates track changes (red, teal, etc.)
        if (color) {
          const colorVal = color.getAttribute('val') || 
                          color.getAttribute('w:val') ||
                          color.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'val');
          console.log(`${indent}[TrackChangesParser] Color value: ${colorVal}`);
          if (colorVal) {
            const upperVal = colorVal.toUpperCase();
            // Check for red colors
            if (upperVal.includes('FF0000') || 
                upperVal.includes('DC2626') ||
                upperVal.includes('0000FF') || // BGR format
                upperVal.endsWith('0000') ||
                upperVal === 'FF0000' ||
                upperVal === 'DC2626') {
              isRedColor = true;
              isTrackChangeColor = true;
            }
            // Check for teal/cyan colors (008080) which Word also uses for track changes
            else if (upperVal.includes('008080') || 
                     upperVal.includes('808000') || // BGR format for teal
                     upperVal === '008080') {
              isTrackChangeColor = true;
            }
          }
        }
        
        isDeletion = !!strike;
        // Insertion: underline with track change color (red or teal)
        // Word uses underline for insertions:
        // - Red (FF0000) for insertions
        // - Teal (008080) for insertions (common in Word Online)
        // If underline is present with a recognized track change color, it's an insertion
        isInsertion = !!underline && (isTrackChangeColor || isRedColor);
        
        console.log(`${indent}[TrackChangesParser] Run formatting: strike=${!!strike}, underline=${!!underline}, color=${!!color}, redColor=${isRedColor}, isDeletion=${isDeletion}, isInsertion=${isInsertion}`);
      } else {
        console.log(`${indent}[TrackChangesParser] No rPr element found in run`);
      }
      
      // Extract text from the run
      let runText = '';
      Array.from(element.childNodes).forEach((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent || '';
          if (text.length > 0) {
            console.log(`${indent}  [TrackChangesParser] Run child ${index}: TEXT_NODE "${text}"`);
            runText += text;
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const childElement = child as Element;
          const childLocalName = childElement.localName || childElement.tagName.split(':').pop();
          if (childLocalName === 't') {
            const text = TrackChangesParser.getTextNodeContent(childElement);
            console.log(`${indent}  [TrackChangesParser] Run child ${index}: w:t "${text}"`);
            runText += text;
          } else if (childLocalName !== 'rPr') {
            // Process other elements but not rPr (formatting)
            console.log(`${indent}  [TrackChangesParser] Run child ${index}: ELEMENT ${childElement.tagName}`);
            runText += this.processElement(childElement, depth + 1);
          }
        }
      });
      
      // Apply track changes styling based on formatting (strip REF from run text if present)
      if (runText.length > 0) {
        if (runText.includes('REF') || runText.includes('MERGEFORMAT')) {
          runText = stripRefPatternsInTextNode(runText);
        }
        if (runText) {
          if (isDeletion) {
            console.log(`${indent}[TrackChangesParser] Marking as deletion: "${runText}"`);
            html += `<del class="track-change-deletion">${this.escapeHtml(runText)}</del>`;
          } else if (isInsertion) {
            console.log(`${indent}[TrackChangesParser] Marking as insertion: "${runText}"`);
            html += `<ins class="track-change-insertion">${this.escapeHtml(runText)}</ins>`;
          } else {
            html += this.escapeHtml(runText);
          }
        }
      }
      
      return html;
    }
    
    // For other elements, process children recursively
    console.log(`${indent}[TrackChangesParser] Processing ${element.childNodes.length} children of ${fullTagName}`);
    Array.from(element.childNodes).forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || '';
        if (text.length > 0) {
          console.log(`${indent}  [TrackChangesParser] Child ${index}: TEXT_NODE "${text}"`);
          html += this.escapeHtml(text);
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const childLocalName = childElement.localName || childElement.tagName.split(':').pop();
        console.log(`${indent}  [TrackChangesParser] Child ${index}: ELEMENT ${childElement.tagName} (localName: ${childLocalName})`);
        html += this.processElement(childElement, depth + 1);
      }
    });
    
    console.log(`${indent}[TrackChangesParser] Finished processing ${fullTagName}, HTML length: ${html.length}`);
    return html;
  }
  
  /**
   * After REF sanitization: remove field scaffold runs, whitespace-only text nodes,
   * and duplicate appendix display (א׳) from REF cross-reference fields.
   */
  static cleanupFieldArtifactsInParagraph(paragraph: Element): void {
    const runsToRemove: Element[] = [];
    const walkRuns = (el: Element) => {
      const localName = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
      if (localName === 'r') {
        let hasFld = false;
        let hasVisibleText = false;
        for (const child of Array.from(el.childNodes)) {
          if (child.nodeType !== Node.ELEMENT_NODE) continue;
          const c = child as Element;
          const cl = (c.localName || c.tagName.split(':').pop() || '').toLowerCase();
          if (cl === 'fldchar' || cl === 'fldsimple') hasFld = true;
          if (cl === 't' && (c.textContent || '').trim()) hasVisibleText = true;
        }
        if (hasFld && !hasVisibleText) runsToRemove.push(el);
      }
      Array.from(el.children).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) walkRuns(child as Element);
      });
    };
    walkRuns(paragraph);
    runsToRemove.forEach((r) => r.parentNode?.removeChild(r));

    const tNodes: Element[] = [];
    const collectT = (el: Element) => {
      const ln = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
      if (ln === 't') tNodes.push(el);
      Array.from(el.children).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) collectT(child as Element);
      });
    };
    collectT(paragraph);

    // Do not clear whitespace-only w:t — Word uses space-only runs between words (e.g. "או" + " cost+").

    for (let i = 0; i < tNodes.length; i++) {
      const prevText = i > 0 ? tNodes[i - 1].textContent || '' : '';
      const curText = tNodes[i].textContent || '';
      const nextText = i + 1 < tNodes.length ? tNodes[i + 1].textContent || '' : '';

      if (
        isAppendixLetterOnly(curText) &&
        isAppendixLetterOnly(nextText) &&
        /נספח\s*$/i.test(prevText.replace(/\s+$/, ''))
      ) {
        tNodes[i + 1].textContent = '';
        TrackChangesParser.removeRunIfEmpty(tNodes[i + 1]);
      }

      if (
        isAppendixLetterOnly(curText) &&
        isAppendixLetterOnly(prevText) &&
        i > 1 &&
        /נספח\s*$/i.test((tNodes[i - 2].textContent || '').replace(/\s+$/, ''))
      ) {
        tNodes[i].textContent = '';
        TrackChangesParser.removeRunIfEmpty(tNodes[i]);
      }
    }
  }

  private static removeRunIfEmpty(tNode: Element): void {
    const run = tNode.parentElement;
    if (!run) return;
    const ln = (run.localName || run.tagName.split(':').pop() || '').toLowerCase();
    if (ln !== 'r') return;
    const hasText = Array.from(run.childNodes).some((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE) return false;
      const c = child as Element;
      const cl = (c.localName || c.tagName.split(':').pop() || '').toLowerCase();
      return cl === 't' && (c.textContent || '').length > 0;
    });
    if (!hasText) run.parentNode?.removeChild(run);
  }

  /**
   * Remove REF from the XML structure before rendering: (1) remove w:instrText elements,
   * (2) strip REF patterns from text in w:delText and w:t so they are not shown (matches courtyard ParagraphXMLDisplay).
   */
  private static removeRefFromTree(root: Element): void {
    const toRemove: Element[] = [];
    const walk = (el: Element) => {
      const localName = el.localName ?? el.tagName?.split(':').pop() ?? '';
      const tagName = el.tagName ?? '';
      if (localName === 'instrText' || tagName.endsWith(':instrText') || tagName.includes('instrText')) {
        toRemove.push(el);
        return;
      }
      if (localName === 'delText' || tagName.includes('delText')) {
        if (el.textContent && (el.textContent.includes('REF') || el.textContent.includes('MERGEFORMAT'))) {
          el.textContent = stripRefPatternsInTextNode(el.textContent);
        }
      }
      if (localName === 't' || tagName.endsWith(':t')) {
        let tc = el.textContent || '';
        if (tc && (tc.includes('REF') || tc.includes('MERGEFORMAT'))) {
          tc = stripRefPatternsInTextNode(tc);
        }
        const normalized = normalizeAppendixFieldText(tc);
        if (normalized !== el.textContent) {
          el.textContent = normalized;
        }
      }
      Array.from(el.children).forEach(child => walk(child as Element));
    };
    walk(root);
    toRemove.forEach(el => el.parentNode?.removeChild(el));

    const rootLocal = root.localName || root.tagName.split(':').pop() || '';
    if (rootLocal === 'p') {
      TrackChangesParser.cleanupFieldArtifactsInParagraph(root);
    } else {
      const para = root.querySelector('p') as Element | null;
      if (para) TrackChangesParser.cleanupFieldArtifactsInParagraph(para);
    }
  }

  /**
   * Check if an element has a descendant with the given local name (e.g. 'del')
   */
  private static hasNestedElement(element: Element, localName: string): boolean {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const childElem = child as Element;
      const childLocal = childElem.localName || childElem.tagName.split(':').pop() || childElem.tagName;
      if (childLocal === localName) return true;
      if (this.hasNestedElement(childElem, localName)) return true;
    }
    return false;
  }

  /**
   * Extract text from an element (DOM-based, like ParagraphXMLDisplay)
   * This extracts only the visible text, ignoring XML structure
   */
  private static extractTextFromElement(element: Element, isDeletion: boolean = false): string {
    let text = '';
    Array.from(element.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent || '';
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const childLocalName = childElement.localName || childElement.tagName.split(':').pop() || childElement.tagName;
        
        // Skip w:del elements when extracting text for insertions
        if (!isDeletion && (childLocalName === 'del' || childElement.tagName.includes('del'))) {
          return;
        }
        
        // For deletions, look for w:delText elements
        if (isDeletion && (childLocalName === 'delText' || childElement.tagName.includes('delText'))) {
          text += childElement.textContent || '';
        } 
        // For insertions and normal text, look for w:t elements
        else if (childLocalName === 't' || childElement.tagName.includes('t')) {
          text += TrackChangesParser.getTextNodeContent(childElement);
        } 
        // For w:r (run) elements, recursively extract text
        else if (childLocalName === 'r' || childElement.tagName.includes('r')) {
          text += this.extractTextFromElement(childElement, isDeletion);
        }
        // Skip Word field instructions (w:instrText) — REF and similar; do not include in extracted text
        else if (childLocalName === 'instrText' || childElement.tagName.includes('instrText')) {
          return;
        }
        // For other elements, recursively extract
        else if (!(!isDeletion && (childLocalName === 'del' || childElement.tagName.includes('del')))) {
          text += this.extractTextFromElement(childElement, isDeletion);
        }
      }
    });
    if (text && (text.includes('REF') || text.includes('MERGEFORMAT'))) {
      text = stripRefPatterns(text);
    }
    return text;
  }

  /**
   * Extract the "accepted" (final) text from OOXML with track changes.
   * Includes: regular runs + inserted text (<w:ins>)
   * Excludes: deleted text (<w:del>)
   *
   * This is the text that would appear if all track changes were accepted.
   */
  static extractAcceptedText(xml: string): string {
    if (!xml) return '';
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(safeXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return stripRefPatterns(this.extractPlainTextFromXml(xml));
      }
      const raw = this.walkForText(xmlDoc.documentElement, 'accepted');
      return stripRefPatterns(raw);
    } catch {
      return stripRefPatterns(this.extractPlainTextFromXml(xml));
    }
  }

  /**
   * Extract the "original" (before changes) text from OOXML with track changes.
   * Includes: regular runs + deleted text (<w:del>)
   * Excludes: inserted text (<w:ins>)
   *
   * This is the text that would appear if all track changes were rejected.
   */
  static extractOriginalText(xml: string): string {
    if (!xml) return '';
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(safeXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return this.extractPlainTextFromXml(xml);
      }
      return this.walkForText(xmlDoc.documentElement, 'original');
    } catch {
      return this.extractPlainTextFromXml(xml);
    }
  }

  /**
   * The text as the given authors left it: their revisions accepted, everyone
   * else's rejected. On a client-requests document this is the client's
   * request even after our responses were tracked on top of it — the accepted
   * text has become our response, and the text Word shows interleaves both.
   */
  static extractTextAcceptingAuthors(xml: string, authors: ReadonlySet<string>): string {
    if (!xml) return '';
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const xmlDoc = new DOMParser().parseFromString(safeXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) return '';
      return stripRefPatterns(this.walkForText(xmlDoc.documentElement, 'authors', authors));
    } catch {
      return '';
    }
  }

  /**
   * Walk XML DOM tree and collect text based on mode:
   * - 'accepted': include <w:ins>, skip <w:del>
   * - 'original': include <w:del>, skip <w:ins>
   * - 'authors': accept the revisions of `authors`, reject everyone else's
   */
  private static walkForText(
    node: Node,
    mode: 'accepted' | 'original' | 'authors',
    authors?: ReadonlySet<string>,
  ): string {
    let text = '';
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as Element;
    const localName = (el.localName || el.tagName.split(':').pop() || el.tagName).toLowerCase();

    // Skip deleted content when extracting accepted text
    if (mode === 'accepted' && (localName === 'del')) {
      return '';
    }
    // Skip inserted content when extracting original text
    if (mode === 'original' && (localName === 'ins')) {
      return '';
    }
    if (mode === 'authors' && (localName === 'ins' || localName === 'del')) {
      const author =
        el.getAttribute('w:author') ||
        el.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
        '';
      const theirs = Boolean(authors?.has(author));
      // Their insertion stays and their deletion goes; anyone else's revision is undone.
      if (localName === 'ins' && !theirs) return '';
      if (localName === 'del' && theirs) return '';
    }

    // Skip Word field instructions (REF … MERGEFORMAT raw); display text lives in w:t runs
    if (localName === 'instrtext') {
      return '';
    }

    // For <w:t> and <w:delText> elements, return their text content directly
    if (localName === 't' || localName === 'deltext') {
      return TrackChangesParser.getTextNodeContent(el);
    }

    // For <w:tab/> and <w:br/>, emit one character so length matches generator's position map
    // (traverseAndMapPositions in RobustXmlTrackChangesGenerator maps these as \t and \n)
    if (localName === 'tab') return '\t';
    if (localName === 'br') return '\n';

    // Recurse into children
    for (const child of Array.from(el.childNodes)) {
      text += this.walkForText(child, mode, authors);
    }

    return text;
  }

  /** Read w:t / w:delText content without dropping space-only runs (xml:space="preserve"). */
  private static getTextNodeContent(textElement: Element): string {
    return textElement.textContent ?? '';
  }

  /**
   * Escapes HTML special characters
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /** Escapes value for safe use inside an HTML attribute. */
  private static escapeHtmlAttribute(value: string): string {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }

  /** Escape for use inside XML text node */
  private static escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  /** True when paragraph OOXML contains Word track-change markup (w:ins / w:del). */
  static hasTrackChangesMarkup(xml: string): boolean {
    if (!xml?.trim()) return false;
    return /<w:(?:ins|del)\b/i.test(xml) || /<(?:\w+:)?(?:ins|del)\b/i.test(xml);
  }

  /** True when OOXML has w:ins/w:del authored by counterparty (not AI / User Edit / Quick Response). */
  static hasCounterpartyTrackChangesInOoxml(ooxml: string): boolean {
    if (!ooxml?.trim()) return false;
    const audit = TrackChangesParser.auditOoxmlTrackChangeAuthors(ooxml);
    return audit.entries.some((entry) => !isOurSideSuggestionAuthor(entry.author));
  }

  /** Strict check — only Word w:ins / w:del elements (avoids false positives after accept transform). */
  static hasWordTrackChangesMarkup(xml: string): boolean {
    if (!xml?.trim()) return false;
    return /<w:(?:ins|del)\b/i.test(xml);
  }

  /** Count opening w:ins / w:del tags (for debug). */
  static countWordTrackChangeTags(xml: string): { ins: number; del: number } {
    return {
      ins: (xml.match(/<w:ins\b/gi) ?? []).length,
      del: (xml.match(/<w:del\b/gi) ?? []).length,
    };
  }

  /** First remaining w:ins / w:del snippet for debug (no PII). */
  static firstWordTrackChangeSnippet(xml: string, maxLen = 160): string {
    const m = xml.match(/<w:(?:ins|del)\b[\s\S]{0,120}/i);
    if (!m) return '';
    const s = m[0].replace(/\s+/g, ' ');
    return s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`;
  }

  /**
   * Remove leftmost w:ins (unwrap) or w:del (drop) using balanced tag scanning.
   * Handles nested markup and self-closing tags that break naive regex.
   */
  private static stripLeftmostWordTrackChangeBlock(xml: string): { xml: string; changed: boolean } {
    const openIns = xml.search(/<w:ins\b/i);
    const openDel = xml.search(/<w:del\b/i);
    if (openIns < 0 && openDel < 0) return { xml, changed: false };

    const tag: 'ins' | 'del' =
      openDel >= 0 && (openIns < 0 || openDel <= openIns) ? 'del' : 'ins';
    const start = tag === 'del' ? openDel : openIns;

    const openTagEnd = xml.indexOf('>', start);
    if (openTagEnd < 0) return { xml, changed: false };

    if (xml.charAt(openTagEnd - 1) === '/') {
      return {
        xml: xml.slice(0, start) + xml.slice(openTagEnd + 1),
        changed: true,
      };
    }

    const openTagRe = new RegExp(`<w:${tag}\\b`, 'gi');
    const closeTagRe = new RegExp(`<\\/w:${tag}\\s*>`, 'gi');

    let depth = 1;
    let pos = openTagEnd + 1;

    while (depth > 0 && pos < xml.length) {
      openTagRe.lastIndex = pos;
      closeTagRe.lastIndex = pos;
      const nextOpen = openTagRe.exec(xml);
      const nextClose = closeTagRe.exec(xml);
      if (!nextClose) break;

      const openIdx = nextOpen?.index ?? Number.POSITIVE_INFINITY;
      const closeIdx = nextClose.index;

      if (closeIdx <= openIdx) {
        depth--;
        if (depth === 0) {
          const end = closeIdx + nextClose[0].length;
          const inner = xml.slice(openTagEnd + 1, closeIdx);
          const replacement = tag === 'ins' ? inner : '';
          return {
            xml: xml.slice(0, start) + replacement + xml.slice(end),
            changed: true,
          };
        }
        pos = closeIdx + nextClose[0].length;
      } else {
        depth++;
        const nestedOpenEnd = xml.indexOf('>', openIdx);
        pos = nestedOpenEnd >= 0 ? nestedOpenEnd + 1 : openIdx + nextOpen![0].length;
      }
    }

    return { xml, changed: false };
  }

  /** True when paragraph OOXML parses as well-formed XML (after namespace sanitization). */
  static paragraphXmlParses(xml: string): boolean {
    if (!xml?.trim()) return false;
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const doc = new DOMParser().parseFromString(safeXml, 'text/xml');
      return !doc.querySelector('parsererror');
    } catch {
      return false;
    }
  }

  /**
   * Single-pass DOM tree walk — unwrap w:ins, drop w:del. O(n), no getElementsByTagName('*') loop.
   * Returns well-formed serialized XML when input parses.
   */
  private static acceptTrackChangesViaDomTreeWalk(xml: string): string | null {
    try {
      const safeXml = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(xml),
      );
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(safeXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) return null;

      const strip = (el: Element): void => {
        let i = 0;
        while (i < el.childNodes.length) {
          const child = el.childNodes[i];
          if (child.nodeType !== Node.ELEMENT_NODE) {
            i++;
            continue;
          }
          const childEl = child as Element;
          const ln = (childEl.localName || childEl.tagName.split(':').pop() || '').toLowerCase();
          if (ln === 'del') {
            el.removeChild(childEl);
            continue;
          }
          if (ln === 'ins') {
            while (childEl.firstChild) {
              el.insertBefore(childEl.firstChild, childEl);
            }
            el.removeChild(childEl);
            continue;
          }
          strip(childEl);
          i++;
        }
      };

      const root = xmlDoc.documentElement;
      if (!root) return null;
      strip(root);
      return new XMLSerializer().serializeToString(root);
    } catch {
      return null;
    }
  }

  /**
   * Apply "accept all" to paragraph OOXML: unwrap insertions, remove deletions.
   *
   * Prefer DOM tree walk (well-formed output for insertOoxml). Fall back to balanced tag
   * scanning when DOM parse fails.
   */
  static acceptAllTrackChangesInOoxml(xml: string): string {
    if (!xml?.trim() || !this.hasTrackChangesMarkup(xml)) return xml;

    const domAccepted = this.acceptTrackChangesViaDomTreeWalk(xml);
    if (domAccepted && !this.hasWordTrackChangesMarkup(domAccepted)) {
      return domAccepted;
    }

    let result = xml;
    for (let guard = 0; guard < 500; guard++) {
      const step = this.stripLeftmostWordTrackChangeBlock(result);
      if (!step.changed) break;
      result = step.xml;
    }

    return result;
  }

  /**
   * Build minimal OOXML for a single paragraph (w:p with one w:r/w:t).
   * Used when sending plain text (e.g. Bank of Changes) to APIs that expect paragraph XML.
   */
  static createMinimalParagraphOoxml(plainText: string): string {
    const escaped = this.escapeXml(plainText || '');
    const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    return `<w:p xmlns:w="${ns}"><w:r><w:t xml:space="preserve">${escaped}</w:t></w:r></w:p>`;
  }

  /**
   * Append a single w:ins suffix to existing paragraph OOXML without a word-level
   * diff. QR labels like [ראה תיקון מוצע] must not go through LCS — CRs that
   * already contain `[]` get garbled (extra ], split words, no clean blue run).
   */
  static appendTrackedSuffixToParagraphOoxml(
    paragraphOoxml: string,
    suffix: string,
    authorName: string,
  ): string {
    const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const date = new Date().toISOString();
    const insId = String((Date.now() % 900000) + 100000);
    const authorAttr = this.escapeHtmlAttribute(authorName || 'Quick Response');
    const textEsc = this.escapeXml(suffix);
    const ins =
      `<w:ins w:id="${insId}" w:author="${authorAttr}" w:date="${date}">` +
      `<w:r><w:t xml:space="preserve">${textEsc}</w:t></w:r></w:ins>`;
    const src = (paragraphOoxml || '').trim();
    if (!src) {
      return `<w:p xmlns:w="${ns}">${ins}</w:p>`;
    }
    const close = src.lastIndexOf('</w:p>');
    if (close < 0) {
      return `<w:p xmlns:w="${ns}">${ins}</w:p>`;
    }
    return `${src.slice(0, close)}${ins}${src.slice(close)}`;
  }

  /**
   * Flat paragraph OOXML for Submit as-is: one w:del (entire accepted baseline) + one w:ins (new plain text).
   * Avoids word-level diff against nested counterparty w:ins (insertOoxml rejects nested ins).
   */
  static createSubmitAsIsParagraphOoxml(
    acceptedPlainText: string,
    newPlainText: string,
    authorName: string,
    sourceParagraphOoxml?: string | null,
  ): string {
    const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const date = new Date().toISOString();
    const delId = String((Date.now() % 900000) + 100000);
    const insId = String((Date.now() % 900000) + 100001);
    const authorAttr = this.escapeHtmlAttribute(authorName || 'User');
    const oldEsc = this.escapeXml(acceptedPlainText || '');
    const newEsc = this.escapeXml(newPlainText || '');

    let pPrXml = '';
    const src = (sourceParagraphOoxml || '').trim();
    if (src) {
      try {
        const safeSrc = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
          TrackChangesParser.deduplicateXmlnsAttributes(src),
        );
        const doc = new DOMParser().parseFromString(safeSrc, 'text/xml');
        if (!doc.querySelector('parsererror')) {
          const all = doc.getElementsByTagName('*');
          for (let i = 0; i < all.length; i++) {
            const el = all[i];
            const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
            if (local === 'ppr') {
              pPrXml = new XMLSerializer().serializeToString(el);
              break;
            }
          }
        }
      } catch {
        /* keep empty pPr */
      }
    }

    return (
      `<w:p xmlns:w="${ns}">` +
      pPrXml +
      `<w:del w:id="${delId}" w:author="${authorAttr}" w:date="${date}">` +
      `<w:r><w:delText xml:space="preserve">${oldEsc}</w:delText></w:r></w:del>` +
      `<w:ins w:id="${insId}" w:author="${authorAttr}" w:date="${date}">` +
      `<w:r><w:t xml:space="preserve">${newEsc}</w:t></w:r></w:ins>` +
      `</w:p>`
    );
  }

  /**
   * Flat paragraph OOXML for a new tracked insertion (w:ins only) with explicit author.
   * When copying pPr from a source paragraph, list numbering (numPr) is stripped — new
   * paragraphs must not inherit the anchor's list definition or insertOoxml rejects them.
   */
  static createTrackedInsertionParagraphOoxml(
    plainText: string,
    authorName: string,
    sourceParagraphOoxml?: string | null,
  ): string {
    const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const date = new Date().toISOString();
    const insId = String((Date.now() % 900000) + 100000);
    const authorAttr = this.escapeHtmlAttribute(authorName || 'Unknown');
    const textEsc = this.escapeXml(plainText || '');
    const pPrXml = TrackChangesParser.extractPPrForNewInsertion(sourceParagraphOoxml);

    return (
      `<w:p xmlns:w="${ns}">` +
      pPrXml +
      `<w:ins w:id="${insId}" w:author="${authorAttr}" w:date="${date}">` +
      `<w:r><w:t xml:space="preserve">${textEsc}</w:t></w:r></w:ins>` +
      `</w:p>`
    );
  }

  /**
   * Rewrite w:author on all w:ins / w:del in a paragraph OOXML fragment.
   * Used after insertText with track changes — Word assigns the logged-in user, then we
   * round-trip via getOoxml + author override + insertOoxml replace.
   */
  static rewriteTrackChangeAuthorsInParagraphOoxml(
    paragraphOoxml: string,
    authorName: string,
  ): string {
    const src = (paragraphOoxml || '').trim();
    if (!src) return src;

    const authorAttr = this.escapeHtmlAttribute(authorName || 'Unknown');
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

    try {
      const safeSrc = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(src),
      );
      const wrapped = `<root xmlns:w="${W_NS}">${safeSrc}</root>`;
      const doc = new DOMParser().parseFromString(wrapped, 'text/xml');
      if (doc.querySelector('parsererror')) {
        return src.replace(/\bw:author="[^"]*"/g, `w:author="${authorAttr}"`);
      }

      const all = doc.getElementsByTagName('*');
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
        if (local === 'ins' || local === 'del') {
          el.setAttributeNS(W_NS, 'author', authorName || 'Unknown');
          el.setAttribute('w:author', authorAttr);
        }
      }

      const pEl = doc.getElementsByTagNameNS(W_NS, 'p').item(0);
      if (pEl) {
        return new XMLSerializer().serializeToString(pEl);
      }
      return src.replace(/\bw:author="[^"]*"/g, `w:author="${authorAttr}"`);
    } catch {
      return src.replace(/\bw:author="[^"]*"/g, `w:author="${authorAttr}"`);
    }
  }

  /** Copy paragraph properties safe for a brand-new paragraph (no list numbering). */
  static extractPPrForNewInsertion(sourceParagraphOoxml?: string | null): string {
    const src = (sourceParagraphOoxml || '').trim();
    if (!src) return '';

    try {
      const safeSrc = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(
        TrackChangesParser.deduplicateXmlnsAttributes(src),
      );
      const doc = new DOMParser().parseFromString(safeSrc, 'text/xml');
      if (doc.querySelector('parsererror')) return '';

      let pPrEl: Element | null = null;
      const all = doc.getElementsByTagName('*');
      for (let i = 0; i < all.length; i++) {
        const el = all[i];
        const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
        if (local === 'ppr') {
          pPrEl = el;
          break;
        }
      }
      if (!pPrEl) return '';

      const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
      const clone = pPrEl.cloneNode(true) as Element;
      const toRemove: Element[] = [];
      const walk = clone.getElementsByTagName('*');
      for (let i = 0; i < walk.length; i++) {
        const el = walk[i];
        const local = (el.localName || el.tagName.split(':').pop() || '').toLowerCase();
        if (local === 'numpr') toRemove.push(el);
        if (local === 'pstyle') {
          const val =
            el.getAttributeNS(W_NS, 'val') ||
            el.getAttribute('w:val') ||
            el.getAttribute('val') ||
            '';
          if (val === 'ListParagraph' || val === 'ListNumber') toRemove.push(el);
        }
      }
      for (const el of toRemove) {
        el.parentNode?.removeChild(el);
      }

      clone.removeAttribute('xmlns:w');
      clone.removeAttribute('xmlns');

      return new XMLSerializer().serializeToString(clone);
    } catch {
      return '';
    }
  }
}
